import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';

const translations = {
  fr: {
    loading: "Chargement de la chronologie...",
    error: "Erreur lors du chargement des données. Veuillez réessayer.",
    timeline: "Chronologie",
    present: "Présent",
  },
  en: {
    loading: "Loading timeline...",
    error: "Error loading data. Please try again.",
    timeline: "Timeline",
    present: "Present",
  }
};

const Chronologie = ({ language, addActiveFile }) => {
  const [chronologieItems, setChronologieItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = useCallback((key) => translations[language][key] || key, [language]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [education, workExperience, projects] = await Promise.all([
          axios.get(`${API_URL}/api/education/?lang=${language}`),
          axios.get(`${API_URL}/api/work-experience/?lang=${language}`),
          axios.get(`${API_URL}/api/projects/?lang=${language}`)
        ]);

        const items = [
          ...education.data.map(edu => ({
            title: edu.degree,
            subtitle: edu.institution,
            startDate: edu.start_date,
            endDate: edu.end_date,
            description: edu.description,
            type: 'education'
          })),
          ...workExperience.data.map(exp => ({
            title: exp.position,
            subtitle: exp.company,
            startDate: exp.start_date,
            endDate: exp.end_date,
            description: exp.short_description,
            type: 'work'
          })),
          ...(projects && projects.data ? projects.data.map(proj => ({
            title: proj.title,
            subtitle: 'Project',
            startDate: proj.start_date || new Date().toISOString(),
            endDate: proj.end_date || null,
            description: proj.short_description || '',
            type: proj.is_course_project ? 'course' : 'project'
          })) : [])
        ].sort((a, b) => {
          if (a.endDate === null || a.endDate === 'Présent') return -1;
          if (b.endDate === null || b.endDate === 'Présent') return 1;
          return new Date(b.startDate) - new Date(a.startDate);
        });

        setChronologieItems(items);
        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données de la chronologie:', error);
        setError('Erreur lors du chargement des données. Veuillez réessayer.');
        setIsLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const handleItemClick = useCallback((item) => {
    const gitTreeContent = `* commit 1a2b3c4d (HEAD -> master, origin/master, origin/HEAD)
| Author: Sami Cakiral
| Date:   ${new Date(item.endDate || new Date()).toLocaleDateString()}
| 
|     [FEAT] Delivered: ${item.title}
|     
|     ${item.description.split('\n').map(line => `      ${line}`).join('\n')}
|
* commit 9f8e7d6c
| Author: Sami Cakiral
| Date:   ${new Date((new Date(item.startDate).getTime() + new Date(item.endDate || new Date()).getTime()) / 2).toLocaleDateString()}
| 
|     [WIP] Core implementation for ${item.subtitle}
|     
|       - Working iteratively on core features
|       - Setting up architecture
|
* commit 5b4a3c2d
  Author: Sami Cakiral
  Date:   ${new Date(item.startDate).toLocaleDateString()}
  
      [INIT] Joined ${item.subtitle} / Started ${item.title}
      
        - Onboarding and project setup
`;
    addActiveFile({
      name: `${item.title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}.git`,
      section: 'Timeline',
      content: gitTreeContent
    });
  }, [addActiveFile]);

  const handleGlobalTimelineClick = useCallback(() => {
    const generateHash = () => Math.random().toString(16).substring(2, 9);

    const getTrack = (type) => {
      if (type === 'work') return 0;
      if (type === 'project') return 1;
      if (type === 'course') return 2;
      if (type === 'education') return 3;
      return 0;
    };

    // Find the min and max indices for each track to know when to start/stop lines
    const trackBounds = {};
    chronologieItems.forEach((item, i) => {
      const t = getTrack(item.type);
      if (!trackBounds[t]) trackBounds[t] = { min: i, max: i, type: item.type };
      trackBounds[t].max = i;
      if (trackBounds[t].min > i) trackBounds[t].min = i;
    });

    const commits = chronologieItems.map((item, i) => {
      const track = getTrack(item.type);
      const dateStr = item.startDate ? new Date(item.startDate).toLocaleDateString() : 'Unknown Date';
      const title = item.title ? item.title.split('\n')[0] : 'Untitled';

      // An active branch has an item at index <= i AND index >= i
      const activeBranches = [];
      Object.keys(trackBounds).forEach(tKey => {
        const t = parseInt(tKey);
        if (i >= trackBounds[t].min && i <= trackBounds[t].max) {
          activeBranches.push({ track: t, type: trackBounds[t].type });
        }
      });

      let isStart = i === trackBounds[track].min; // Formally the newest item (top of screen), but visually the line doesn't go UP from here.
      let isEnd = i === trackBounds[track].max;   // Formally the oldest item (bottom of screen), no line going DOWN.

      return {
        hash: generateHash(),
        title: `[${item.type.toUpperCase()}] ${title}`,
        subtitle: item.subtitle || '',
        date: dateStr,
        author: 'Sami',
        type: item.type,
        track: track,
        activeBranches: activeBranches,
        isStart: isStart,
        isEnd: isEnd
      };
    });

    // Genesis commit
    commits.push({
      hash: generateHash(),
      title: '[INIT] System Boot',
      subtitle: 'Hello World',
      date: 'Genesis',
      author: 'Sami',
      type: 'main',
      track: 0,
      activeBranches: [{ track: 0, type: 'work' }],
      isStart: false,
      isEnd: true
    });

    addActiveFile({
      name: `global_timeline.gitgraph`,
      section: 'Timeline',
      content: JSON.stringify(commits)
    });
  }, [chronologieItems, addActiveFile]);

  if (isLoading) return <div className="p-4">{t('loading')}</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="w-full h-full bg-[var(--bg-secondary)] text-[var(--text-primary)] flex flex-col overflow-hidden">
      <div
        className="p-2 pl-3 text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)] hover:text-blue-400 cursor-pointer flex items-center transition-colors group"
        onClick={handleGlobalTimelineClick}
        title="View Global Git Tree"
      >
        <span className="group-hover:text-blue-400 transition-colors mr-2">⎇</span>
        {t('timeline')}
      </div>
      <div className="flex-1 overflow-y-auto p-4 pt-2">
        <div className="flex flex-col space-y-4">
          {chronologieItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start cursor-pointer hover:bg-[var(--hover-bg)]"
              onClick={() => handleItemClick(item)}
            >
              <div className={`w-3 h-3 min-w-[0.75rem] mt-1 rounded-full ${item.type === 'education' ? 'bg-blue-500' : (item.type === 'project' ? 'bg-purple-500' : 'bg-green-500')
                }`}></div>
              <div className="w-10 h-px bg-gray-600 mx-2 mt-2"></div>
              <div className="flex flex-col">
                <div className="text-sm font-semibold">{item.title}</div>
                <div className="text-xs">{item.subtitle}</div>
                <div className="text-xs text-gray-500">
                  {new Date(item.startDate).toLocaleDateString()} -
                  {item.endDate ? new Date(item.endDate).toLocaleDateString() : t('present')}
                </div>
                <div className="text-xs mt-1">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Chronologie;
